// Sugalarm — Wi-Fi signal hook (cycle 235.4).
//
// Returns { available, connected, level, percent } for the header Wi-Fi
// indicator. Fully inert (available:false, level:-1) off the Android kiosk
// APK — the `lib/wifiSignal` proxy is a no-op everywhere the native plugin
// isn't present (web, Pi kiosk, iPad, mobile, the `standard` flavor).
// Mirrors `useBatteryStatus`, plus a light re-poll because RSSI broadcasts
// can be sparse.

import { useEffect, useState } from "react";

import { getStatus, isAvailable, subscribe } from "../lib/wifiSignal";
import type { WifiStatus } from "../lib/wifiSignal";

export interface WifiSignalState {
  available: boolean;
  connected: boolean;
  /** 0–4 bars, or -1 when unknown/disconnected. */
  level: number;
  /** 0–100, or -1 when unknown/disconnected. */
  percent: number;
  /** The connected network's SSID, or null when unknown/disconnected.
   *  (Cycle 247 — surfaced for the Settings → Wi-Fi tile + tab readout.) */
  ssid: string | null;
}

export function useWifiSignal(): WifiSignalState {
  const available = isAvailable();
  const [connected, setConnected] = useState(false);
  const [level, setLevel] = useState(-1);
  const [percent, setPercent] = useState(-1);
  const [ssid, setSsid] = useState<string | null>(null);

  useEffect(() => {
    if (!available) return;
    let active = true;
    const apply = (s: WifiStatus): void => {
      setConnected(s.connected);
      setLevel(s.level);
      setPercent(s.percent);
      setSsid(s.ssid ?? null);
    };
    void getStatus().then((s) => {
      if (active && s) apply(s);
    });
    const unsub = subscribe((s) => apply(s));
    // RSSI_CHANGED_ACTION can be infrequent — keep the readout fresh with a
    // light periodic re-read on top of the event stream.
    const id = window.setInterval(() => {
      void getStatus().then((s) => {
        if (active && s) apply(s);
      });
    }, 15_000);
    return () => {
      active = false;
      unsub();
      window.clearInterval(id);
    };
  }, [available]);

  return { available, connected, level, percent, ssid };
}
